import { useMutation } from "@apollo/client";
import { Link as MuiLink, Stack, Typography } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { StatusChip } from "components/StatusChip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { RuleSuggestionsQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import {
  GET_RULE_SUGGESTIONS,
  REFRESH_RULE_SUGGESTION_LINEAR_STATUSES,
} from "hooks/useRuleSuggestions";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { confidenceColor, deriveSuggestionStatus } from "./derivedStatus";
import { RuleSuggestionDrawer } from "./RuleSuggestionDrawer";

type RuleSuggestionRow = RuleSuggestionsQuery["ruleSuggestions"]["items"][number];

// ── Stable, module-level config (V2 requires stable prop references) ──
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "lastSuggestedAt", sort: "desc" }];
const FILTER_GROUPS: FilterGroup[] = [
  { name: "Status", fields: ["status"] },
  { name: "Source", fields: ["source"] },
  { name: "Confidence", fields: ["confidence"] },
];

const getItems = (data: RuleSuggestionsQuery) => data.ruleSuggestions.items ?? [];
const getCount = (data: RuleSuggestionsQuery) => data.ruleSuggestions.count ?? 0;

const SOURCE_LABELS: Record<string, string> = {
  CERS_CUPA_PREFERENCE: "CERS · CUPA preference",
};

export const RuleSuggestions = () => {
  useBreadcrumb({ label: "Rule Suggestions", to: "/staff/rule-suggestions" });

  const [searchParams, setSearchParams] = useSearchParams();
  const suggestionId = searchParams.get("suggestionId");

  // Stale-while-revalidate: the grid renders from cached linear* columns;
  // one batched refresh patches any drift after first paint.
  // refreshFiredRef prevents the double-fire that StrictMode would cause with useState.
  const refreshFiredRef = useRef(false);
  const [refresh, { error: refreshError }] = useMutation(REFRESH_RULE_SUGGESTION_LINEAR_STATUSES, {
    refetchQueries: ["RuleSuggestions"],
  });
  useEffect(() => {
    if (refreshFiredRef.current) return;
    refreshFiredRef.current = true;
    refresh().catch(() => {
      // surfaced via refreshError; the page stays fully usable on cached data
    });
  }, [refresh]);

  const openSuggestion = useCallback(
    (params: GridRowParams<RuleSuggestionRow>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("suggestionId", String(params.id));
        return next;
      });
    },
    [setSearchParams]
  );

  const closeSuggestion = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("suggestionId");
      return next;
    });
  }, [setSearchParams]);

  const columns: OmnisearchGridColDef<RuleSuggestionRow>[] = useMemo(
    () => [
      { field: "title", headerName: "Title", flex: 1.2 },
      {
        field: "source",
        headerName: "Source",
        flex: 0.6,
        sortable: false,
        renderCell: ({ row }) => (
          <StatusChip
            label={SOURCE_LABELS[row.source] ?? prettyPrintEnumValue(row.source)}
            color="default"
          />
        ),
      },
      { field: "jurisdiction", headerName: "Jurisdiction", flex: 0.8 },
      {
        field: "confidence",
        headerName: "Confidence",
        flex: 0.4,
        renderCell: ({ row }) => {
          if (!row.confidence) return null;
          return <StatusChip label={row.confidence} color={confidenceColor(row.confidence)} />;
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.5,
        renderCell: ({ row }) => {
          const derived = deriveSuggestionStatus(
            row.status,
            row.linearStateName,
            row.linearStateType,
            row.linearIssueIdentifier
          );
          return <StatusChip label={derived.label} color={derived.color} />;
        },
      },
      {
        field: "linearIssueIdentifier",
        headerName: "Linear ticket",
        flex: 0.45,
        renderCell: ({ row }) => {
          if (!row.linearIssueIdentifier || !row.linearIssueUrl) return null;
          return (
            <MuiLink
              href={row.linearIssueUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {row.linearIssueIdentifier}
            </MuiLink>
          );
        },
      },
      {
        field: "lastSuggestedAt",
        headerName: "Last suggested",
        flex: 0.45,
        valueGetter: (_, row) => new Date(row.lastSuggestedAt).toLocaleDateString(),
      },
    ],
    []
  );

  // Default to the "Needs review" work-queue posture when no omnisearch is
  // present. OmnisearchDataGridV2 has no initialSearch prop — per the V2
  // migration guide, defaults belong inside buildVariables.
  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch || 'status:"OPEN"',
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    []
  );

  return (
    <Stack sx={{ pt: 3 }} spacing={2}>
      {/* Match the grid's own horizontal inset (OmnisearchDataGridV2 wraps in px: 3). */}
      <Typography variant="body2" color="text.secondary" sx={{ px: 3 }}>
        Rule ideas surfaced by product features, reviewed here before an engineer adds them to the
        Compliance Engine.
      </Typography>
      {refreshError ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 3 }}>
          Couldn&apos;t refresh Linear statuses — showing last-known values.
        </Typography>
      ) : null}
      <OmnisearchDataGridV2
        query={GET_RULE_SUGGESTIONS}
        getItems={getItems}
        getCount={getCount}
        persistenceKey="staff-rule-suggestions"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialSortModel={INITIAL_SORT_MODEL}
        filterGroups={FILTER_GROUPS}
        columns={columns}
        buildVariables={buildVariables}
        noRowsMessage="No rule suggestions."
        onRowClick={openSuggestion}
      />
      <RuleSuggestionDrawer suggestionId={suggestionId} onClose={closeSuggestion} />
    </Stack>
  );
};
